import { useState, useCallback, useMemo } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';

/**
 * @typedef {Object} DownloadPDFOptions
 * @property {string} [orientation='portrait'] - Orientação do PDF ('portrait' ou 'landscape')
 * @property {string} [unit='mm'] - Unidade de medida do PDF ('mm', 'pt', 'cm', 'in')
 * @property {string|number[]} [format='a4'] - Formato do PDF ('a4', 'letter', etc) ou dimensões personalizadas [width, height]
 * @property {boolean} [compress=true] - Se deve comprimir o PDF
 * @property {number} [margin=10] - Margem em milímetros
 * @property {string} [reportNamePrefix='report'] - Prefixo para o nome do arquivo
 * @property {number} [pixelToMm=0.264583] - Fator de conversão de pixel para milímetros
 */

/**
 * Hook para baixar elementos HTML como PDF.
 * Permite selecionar múltiplos elementos para serem incluídos no mesmo documento PDF.
 *
 * @param {DownloadPDFOptions} [configOptions={}] - Opções para a geração do PDF
 * @returns {Object} Um objeto contendo o estado de carregamento e a função para fazer o download
 */
const useDownloadElementAsPDF = (configOptions = {}) => {
  // Estado que controla se o PDF está sendo gerado
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  // Memoriza as opções para evitar recriações desnecessárias a cada renderização
  const pdfConfigurationOptions = useMemo(
    () => ({
      orientation: configOptions.orientation || 'portrait',
      unit: configOptions.unit || 'mm',
      format: configOptions.format || 'a4',
      compress:
        configOptions.compress !== undefined ? configOptions.compress : true,
      margin: configOptions.margin || 10,
      reportNamePrefix: configOptions.reportNamePrefix || 'report',
      pixelToMm: configOptions.pixelToMm || 0.264583
    }),
    [
      configOptions.orientation,
      configOptions.unit,
      configOptions.format,
      configOptions.compress,
      configOptions.margin,
      configOptions.reportNamePrefix,
      configOptions.pixelToMm
    ]
  );

  // Memoriza a função de renderização para canvas para evitar recriações
  const convertHtmlElementToCanvas = useCallback(async (htmlElement) => {
    if (!htmlElement) return null;

    return html2canvas(htmlElement, {
      scale: 2, // Renderização de maior qualidade
      logging: false // Desativa logs para melhor performance
    });
  }, []);

  // Memoriza a função para adicionar uma imagem ao PDF
  const insertElementImageToPdfDocument = useCallback(
    async (
      pdfDocument,
      htmlElement,
      currentVerticalPosition,
      totalPageHeight,
      contentMaxWidth,
      pageMargin,
      pixelToMillimeterRatio
    ) => {
      // Retorna a altura atual se não houver elemento
      if (!htmlElement) return currentVerticalPosition;

      // Renderiza o elemento em um canvas
      const canvasElement = await convertHtmlElementToCanvas(htmlElement);
      if (!canvasElement) return currentVerticalPosition;

      // Ignora canvases vazios/em branco (verifica se o canvas é quase todo branco)
      // Isso ajuda a evitar páginas em branco
      const ctx = canvasElement.getContext('2d');
      const imageData = ctx.getImageData(
        0,
        0,
        canvasElement.width,
        canvasElement.height
      );
      const data = imageData.data;
      let nonWhitePixels = 0;

      // Amostra o canvas para verificar se ele tem conteúdo
      for (let i = 0; i < data.length; i += 40) {
        // Verifica os valores RGB (ignora o alfa)
        if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
          nonWhitePixels++;
        }
        // Saída antecipada se encontrarmos conteúdo suficiente
        if (nonWhitePixels > 50) break;
      }

      // Ignora a renderização se o canvas for essencialmente branco
      // Mas não atualiza a posição vertical para evitar criação de página em branco
      if (nonWhitePixels < 20) return currentVerticalPosition;

      // Converte o canvas para dados de imagem com leve compressão
      const imageDataUrl = canvasElement.toDataURL('image/png', 0.95);

      // Calcula as dimensões do elemento em milímetros
      const elementHeightInMm = canvasElement.height * pixelToMillimeterRatio;
      const elementWidthInMm = canvasElement.width * pixelToMillimeterRatio;

      let updatedVerticalPosition = currentVerticalPosition;

      // Adiciona nova página apenas se já tivermos conteúdo e o novo elemento não couber
      if (
        updatedVerticalPosition > pageMargin && // Verifica se já renderizamos algo na página atual
        updatedVerticalPosition + elementHeightInMm >=
          totalPageHeight - pageMargin
      ) {
        pdfDocument.addPage();
        updatedVerticalPosition = pageMargin;
      }

      // Escala a imagem proporcionalmente para caber na largura disponível
      const widthScaleFactor = contentMaxWidth / elementWidthInMm;
      const scaledImageWidth = elementWidthInMm * widthScaleFactor;
      const scaledImageHeight = elementHeightInMm * widthScaleFactor;

      // Adiciona a imagem ao PDF na posição calculada
      pdfDocument.addImage(
        imageDataUrl,
        'PNG',
        pageMargin,
        updatedVerticalPosition,
        scaledImageWidth,
        scaledImageHeight
      );

      // Retorna a nova altura para o próximo elemento
      return updatedVerticalPosition + scaledImageHeight + pageMargin;
    },
    [convertHtmlElementToCanvas]
  );

  // Função principal para download do PDF, memorizada para evitar recriações
  const generateAndDownloadPdf = useCallback(
    async (targetElementIds = [], customFileName = null) => {
      // Previne execuções simultâneas
      if (isPdfGenerating) return;

      // Extrai as opções do PDF memorizadas
      const {
        orientation,
        unit,
        format,
        compress,
        margin,
        reportNamePrefix,
        pixelToMm
      } = pdfConfigurationOptions;

      setIsPdfGenerating(true);

      try {
        // Obtém os elementos de forma eficiente com tratamento de erros
        const validHtmlElements = targetElementIds.reduce(
          (collectedElements, elementId) => {
            const domElement = document.getElementById(elementId);
            if (domElement) collectedElements.push(domElement);
            return collectedElements;
          },
          []
        );

        // Verifica se há elementos válidos para exportar
        if (validHtmlElements.length === 0) {
          toast.error('Nenhum elemento válido encontrado para exportar');
          return;
        }

        // Cria o PDF com as opções memorizadas
        const pdfDocument = new jsPDF({
          orientation,
          unit,
          format,
          compress
        });

        // Pré-calcula as dimensões da página uma única vez
        const pageWidth = pdfDocument.internal.pageSize.getWidth();
        const pageHeight = pdfDocument.internal.pageSize.getHeight();
        const availableContentWidth = pageWidth - 2 * margin;

        // Define o nome do relatório com base no parâmetro ou gera um nome com timestamp
        const outputFileName =
          customFileName ||
          `${reportNamePrefix}-${new Date()
            .toISOString()
            .split('.')[0]
            .replace(/:/g, '-')}.pdf`;

        // Processa os elementos sequencialmente com padrão acumulador para a altura da página
        let currentYPosition = margin;

        // Usa for...of para melhor tratamento assíncrono
        for (const htmlElement of validHtmlElements) {
          currentYPosition = await insertElementImageToPdfDocument(
            pdfDocument,
            htmlElement,
            currentYPosition,
            pageHeight,
            availableContentWidth,
            margin,
            pixelToMm
          );
        }

        // Salva o PDF e notifica o usuário
        pdfDocument.save(outputFileName);
        toast.info(`Baixando relatório: ${outputFileName}`);
      } catch (error) {
        // Tratamento de erro com log e notificação
        console.error('Erro ao gerar PDF:', error);
        toast.error('Erro ao gerar o PDF. Tente novamente.');
      } finally {
        // Garante que o estado de carregamento seja desativado
        setIsPdfGenerating(false);
      }
    },
    [pdfConfigurationOptions, insertElementImageToPdfDocument, isPdfGenerating]
  );

  // Memoriza o valor de retorno para evitar renderizações desnecessárias
  return useMemo(
    () => ({
      isPdfGenerating,
      generateAndDownloadPdf
    }),
    [isPdfGenerating, generateAndDownloadPdf]
  );
};

export default useDownloadElementAsPDF;
